import React, { useEffect, useState, useCallback } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Card,
  TablePagination, TextField, Box, Alert, Snackbar, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, CircularProgress, Checkbox, Switch, FormControlLabel,
  IconButton, InputAdornment, Stack, Tooltip
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import DevicesOtherOutlinedIcon from '@mui/icons-material/DevicesOtherOutlined';
import { EmptyState, PageHeader } from '../ui';
import { deviceService } from '../../services/deviceService';
import * as XLSX from 'xlsx';

export const DeviceList: React.FC = () => {
  const [devices, setDevices] = useState<any[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [exactSearch, setExactSearch] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [selectedDevices, setSelectedDevices] = useState<string[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingDevice, setEditingDevice] = useState<any>(null);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPage(0);
    }, 1000);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  const fetchDevices = useCallback(async (currentPage: number, currentRowsPerPage: number, search?: string) => {
    setIsLoading(true);
    try {
      const response = await deviceService.getDevices({
        limit: currentRowsPerPage,
        page: currentPage,
        searchTerm: search,
        exactSearch
      });
      setDevices(response.devices);
      setTotalCount(response.total || 0);
    } catch (error) {
      setError('Failed to fetch devices');
      console.error('Error fetching devices:', error);
    } finally {
      setIsLoading(false);
    }
  }, [exactSearch]);

  useEffect(() => {
    fetchDevices(page, rowsPerPage, debouncedSearchTerm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, exactSearch, debouncedSearchTerm]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
  };

  const handleExactSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setExactSearch(event.target.checked);
    setPage(0);
    //fetchDevices(0, rowsPerPage, searchTerm);
  };

  const handleStatusChange = async (deviceId: string, currentStatus: number) => {
    try {
      const newStatus = currentStatus === 1 ? 0 : 1;
      // Find the current device
      const device = devices.find(d => d.id === deviceId);
      if (!device) return;

      // Create a new object with all fields, only status changed
      const updatedDevice = { ...device, status: newStatus };

      // Only include fields that are allowed in DeviceInput
      const allowedFields = {
        baseband: updatedDevice.baseband,
        bootloader: updatedDevice.bootloader,
        model: updatedDevice.model,
        board: updatedDevice.board,
        hardware: updatedDevice.hardware,
        manufacturer: updatedDevice.manufacturer,
        brand: updatedDevice.brand,
        code: updatedDevice.code,
        platform: updatedDevice.platform,
        product: updatedDevice.product,
        fingerprint: updatedDevice.fingerprint,
        release: updatedDevice.release,
        sdk: updatedDevice.sdk,
        securityPath: updatedDevice.securityPath,
        buildHost: updatedDevice.buildHost,
        buildId: updatedDevice.buildId,
        buildDisplayId: updatedDevice.buildDisplayId,
        buildIncremental: updatedDevice.buildIncremental,
        buildDescription: updatedDevice.buildDescription,
        buildDateUtc: updatedDevice.buildDateUtc,
        buildFlavor: updatedDevice.buildFlavor,
        status: updatedDevice.status,
        custom_note: updatedDevice.custom_note,
        mac_address: updatedDevice.mac_address,
        safety_net: updatedDevice.safety_net,
        gpu: updatedDevice.gpu,
        imei: updatedDevice.imei,
        name: updatedDevice.name,
        country: updatedDevice.country
      };

      const response = await deviceService.updateDevice(deviceId, allowedFields);
      if (response.result) {
        setDevices(prevDevices =>
          prevDevices.map(device =>
            device.id === deviceId
              ? { ...device, status: newStatus }
              : device
          )
        );
        setSuccessMessage('Device status updated successfully');
        //fetchDevices(page, rowsPerPage, searchTerm);
      } else {
        setError(response.message || 'Failed to update device status');
      }
    } catch (error) {
      setError('Failed to update device status');
      console.error('Error updating device status:', error);
    }
  };

  const handleEditClick = (device: any) => {
    setEditingDevice(device);
    setEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    if (!editingDevice) return;

    try {
      // Only include fields that are allowed in DeviceInput
      const allowedFields = {
        baseband: editingDevice.baseband,
        bootloader: editingDevice.bootloader,
        model: editingDevice.model,
        board: editingDevice.board,
        hardware: editingDevice.hardware,
        manufacturer: editingDevice.manufacturer,
        brand: editingDevice.brand,
        code: editingDevice.code,
        platform: editingDevice.platform,
        product: editingDevice.product,
        fingerprint: editingDevice.fingerprint,
        release: editingDevice.release,
        sdk: editingDevice.sdk,
        securityPath: editingDevice.securityPath,
        buildHost: editingDevice.buildHost,
        buildId: editingDevice.buildId,
        buildDisplayId: editingDevice.buildDisplayId,
        buildIncremental: editingDevice.buildIncremental,
        buildDescription: editingDevice.buildDescription,
        buildDateUtc: editingDevice.buildDateUtc,
        buildFlavor: editingDevice.buildFlavor,
        status: editingDevice.status,
        custom_note: editingDevice.custom_note,
        mac_address: editingDevice.mac_address,
        safety_net: editingDevice.safety_net,
        gpu: editingDevice.gpu,
        imei: editingDevice.imei,
        name: editingDevice.name,
        country: editingDevice.country
      };

      const response = await deviceService.updateDevice(editingDevice.id, allowedFields);
      if (response.result) {
        setSuccessMessage('Device updated successfully');
        setEditDialogOpen(false);
        fetchDevices(page, rowsPerPage, searchTerm);
      } else {
        setError(response.message || 'Failed to update device');
      }
    } catch (error) {
      setError('Failed to update device');
      console.error('Error updating device:', error);
    }
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    //fetchDevices(0, newRowsPerPage, searchTerm);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    //fetchDevices(newPage, rowsPerPage, searchTerm);
  };

  const handleFileImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImportLoading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);

          // Transform data to match the required format
          const devices = jsonData.map((row: any) => ({
            release: row.release || '',
            board: row.board || '',
            buildIncremental: row.buildIncremental || '',
            buildDisplayId: row.buildDisplayId || '',
            code: row.code || '',
            hardware: row.hardware || '',
            buildHost: row.buildHost || '',
            sdk: row.sdk || '',
            model: row.model || '',
            product: row.product || '',
            buildDescription: row.buildDescription || '',
            brand: row.brand || '',
            baseband: row.baseband || '',
            buildFlavor: row.buildFlavor || '',
            securityPath: row.securityPath || '',
            fingerprint: row.fingerprint || '',
            buildId: row.buildId || '',
            manufacturer: row.manufacturer || '',
            buildDateUtc: row.buildDateUtc || '',
            platform: row.platform || '',
            bootloader: row.bootloader || '',
            mac_address: row.mac_address || '',
            safety_net: row.safety_net || '',
            gpu: row.gpu || '',
            imei: row.imei || '',
            name: row.name || '',
            country: row.country || ''
          }));

          const response = await deviceService.importDevices(devices);
          if (response.result) {
            setSuccessMessage('Devices imported successfully');
            fetchDevices(page, rowsPerPage, searchTerm);
          } else {
            setError(response.message || 'Failed to import devices');
          }
        } catch (error) {
          console.error('Error processing file:', error);
          setError('Failed to process file');
        } finally {
          setImportLoading(false);
        }
      };
      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.error('Error reading file:', error);
      setError('Failed to read file');
      setImportLoading(false);
    }
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedDevices(devices.map(device => device.id));
    } else {
      setSelectedDevices([]);
    }
  };

  const handleSelectDevice = (deviceId: string) => {
    setSelectedDevices(prev => {
      if (prev.includes(deviceId)) {
        return prev.filter(id => id !== deviceId);
      } else {
        return [...prev, deviceId];
      }
    });
  };

  const handleDeleteSelected = async () => {
    if (selectedDevices.length === 0) return;

    try {
      const response = await deviceService.deleteDevices(selectedDevices);
      if (response.result) {
        setSuccessMessage('Devices deleted successfully');
        setSelectedDevices([]);
        fetchDevices(page, rowsPerPage, searchTerm);
      } else {
        setError(response.message || 'Failed to delete devices');
      }
    } catch (error) {
      setError('Failed to delete devices');
      console.error('Error deleting devices:', error);
    }
    setDeleteDialogOpen(false);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert onClose={() => setError(null)} severity="error">{error}</Alert>
      </Snackbar>
      <Snackbar open={!!successMessage} autoHideDuration={6000} onClose={() => setSuccessMessage(null)}>
        <Alert onClose={() => setSuccessMessage(null)} severity="success">{successMessage}</Alert>
      </Snackbar>
      <PageHeader
        eyebrow="Config"
        title="Devices"
        description="Every device profile in the fleet, with its build fingerprint and activation state."
        actions={
          <>
            {selectedDevices.length > 0 && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setDeleteDialogOpen(true)}
                disabled={isLoading || importLoading}
              >
                Delete {selectedDevices.length} selected
              </Button>
            )}
            <Button
              variant="contained"
              component="label"
              startIcon={<CloudUploadIcon />}
              disabled={isLoading || importLoading}
            >
              Import devices
              <input type="file" hidden accept=".xlsx,.xls" onChange={handleFileImport} />
            </Button>
          </>
        }
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
          <TextField
            sx={{ width: { xs: '100%', sm: 420 } }}
            placeholder="Search by brand, model, serial…"
            value={searchTerm}
            onChange={handleSearchChange}
            disabled={isLoading || importLoading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              ),
            }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={exactSearch}
                onChange={handleExactSearchChange}
                disabled={isLoading || importLoading}
              />
            }
            label="Exact match"
            slotProps={{ typography: { variant: 'body2' } }}
          />
        </Stack>
      </PageHeader>

      <Card>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedDevices.length > 0 && selectedDevices.length < devices.length}
                  checked={devices.length > 0 && selectedDevices.length === devices.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell> </TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Brand</TableCell>
              <TableCell>Model</TableCell>
              <TableCell>Release</TableCell>
              <TableCell>IMEI</TableCell>
              <TableCell>Build Incremental</TableCell>
              <TableCell>Build Display ID</TableCell>
              <TableCell>Fingerprint</TableCell>
              <TableCell>Security Path</TableCell>
              <TableCell>Baseband</TableCell>
              <TableCell>Bootloader</TableCell>
              <TableCell>Board</TableCell>
              <TableCell>Hardware</TableCell>
              <TableCell>Manufacturer</TableCell>
              <TableCell>Code</TableCell>
              <TableCell>Platform</TableCell>
              <TableCell>Product</TableCell>
              <TableCell>SDK</TableCell>
              <TableCell>Build Host</TableCell>
              <TableCell>Build ID</TableCell>
              <TableCell>Build Description</TableCell>
              <TableCell>Build Date UTC</TableCell>
              <TableCell>Build Flavor</TableCell>
              <TableCell>MAC Address</TableCell>
              <TableCell>Safety Net</TableCell>
              <TableCell>GPU</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Country</TableCell>
              <TableCell>Custom Note</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading || importLoading ? (
              <TableRow>
                <TableCell colSpan={29} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={22} />
                </TableCell>
              </TableRow>
            ) : devices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={29} sx={{ p: 0 }}>
                  <EmptyState
                    icon={<DevicesOtherOutlinedIcon />}
                    title="No devices found"
                    description={
                      searchTerm
                        ? 'No device matches that search. Try turning off exact match.'
                        : 'Import an XLSX export to populate the fleet.'
                    }
                  />
                </TableCell>
              </TableRow>
            ) : (
              devices.map((device) => (
                <TableRow key={device.id || device.imei} hover>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedDevices.includes(device.id)}
                      onChange={() => handleSelectDevice(device.id)}
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Edit">
                      <IconButton onClick={() => handleEditClick(device)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={device.status === 1}
                      onChange={() => handleStatusChange(device.id, device.status)}
                      color="primary"
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>{device.brand}</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>{device.model}</TableCell>
                  <TableCell>{device.release}</TableCell>
                  <TableCell className="mono">{device.imei}</TableCell>
                  <TableCell>{device.buildIncremental}</TableCell>
                  <TableCell>{device.buildDisplayId}</TableCell>
                  <TableCell className="mono">{device.fingerprint}</TableCell>
                  <TableCell>{device.securityPath}</TableCell>
                  <TableCell>{device.baseband}</TableCell>
                  <TableCell>{device.bootloader}</TableCell>
                  <TableCell>{device.board}</TableCell>
                  <TableCell>{device.hardware}</TableCell>
                  <TableCell>{device.manufacturer}</TableCell>
                  <TableCell>{device.code}</TableCell>
                  <TableCell>{device.platform}</TableCell>
                  <TableCell>{device.product}</TableCell>
                  <TableCell>{device.sdk}</TableCell>
                  <TableCell>{device.buildHost}</TableCell>
                  <TableCell>{device.buildId}</TableCell>
                  <TableCell>{device.buildDescription}</TableCell>
                  <TableCell>{device.buildDateUtc}</TableCell>
                  <TableCell>{device.buildFlavor}</TableCell>
                  <TableCell>{device.mac_address}</TableCell>
                  <TableCell>{device.safety_net}</TableCell>
                  <TableCell>{device.gpu}</TableCell>
                  <TableCell>{device.name}</TableCell>
                  <TableCell>{device.country}</TableCell>
                  <TableCell>{device.custom_note}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50, 100, 200]}
      />
      </Card>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Selected Devices</DialogTitle>
        <DialogContent>
          Are you sure you want to delete {selectedDevices.length} selected device(s)?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteSelected} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Device</DialogTitle>
        <DialogContent>
          {editingDevice && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                <TextField
                  label="Brand"
                  value={editingDevice.brand || ''}
                  onChange={(e) => setEditingDevice({ ...editingDevice, brand: e.target.value })}
                  fullWidth
                />
                <TextField
                  label="Model"
                  value={editingDevice.model || ''}
                  onChange={(e) => setEditingDevice({ ...editingDevice, model: e.target.value })}
                  fullWidth
                />
                <TextField
                  label="Release"
                  value={editingDevice.release || ''}
                  onChange={(e) => setEditingDevice({ ...editingDevice, release: e.target.value })}
                  fullWidth
                />
                <TextField
                  label="IMEI"
                  value={editingDevice.imei || ''}
                  onChange={(e) => setEditingDevice({ ...editingDevice, imei: e.target.value })}
                  fullWidth
                />
                <TextField
                  label="Board"
                  value={editingDevice.board || ''}
                  onChange={(e) => setEditingDevice({ ...editingDevice, board: e.target.value })}
                  fullWidth
                />
                <TextField
                  label="Hardware"
                  value={editingDevice.hardware || ''}
                  onChange={(e) => setEditingDevice({ ...editingDevice, hardware: e.target.value })}
                  fullWidth
                />
                <TextField
                  label="Manufacturer"
                  value={editingDevice.manufacturer || ''}
                  onChange={(e) => setEditingDevice({ ...editingDevice, manufacturer: e.target.value })}
                  fullWidth
                />
                <TextField
                  label="Product"
                  value={editingDevice.product || ''}
                  onChange={(e) => setEditingDevice({ ...editingDevice, product: e.target.value })}
                  fullWidth
                />
                <TextField
                  label="Platform"
                  value={editingDevice.platform || ''}
                  onChange={(e) => setEditingDevice({ ...editingDevice, platform: e.target.value })}
                  fullWidth
                />
                <TextField
                  label="Code"
                  value={editingDevice.code || ''}
                  onChange={(e) => setEditingDevice({ ...editingDevice, code: e.target.value })}
                  fullWidth
                />
                <TextField
                  label="Build Incremental"
                  value={editingDevice.buildIncremental || ''}
                  onChange={(e) => setEditingDevice({ ...editingDevice, buildIncremental: e.target.value })}
                  fullWidth
                />
                <TextField
                  label="Build Display ID"
                  value={editingDevice.buildDisplayId || ''}
                  onChange={(e) => setEditingDevice({ ...editingDevice, buildDisplayId: e.target.value })}
                  fullWidth
                />
                <TextField
                  label="Build Host"
                  value={editingDevice.buildHost || ''}
                  onChange={(e) => setEditingDevice({ ...editingDevice, buildHost: e.target.value })}
                  fullWidth
                />
                <TextField
                  label="SDK"
                  value={editingDevice.sdk || ''}
                  onChange={(e) => setEditingDevice({ ...editingDevice, sdk: e.target.value })}
                  fullWidth
                />
                <TextField
                  label="Build ID"
                  value={editingDevice.buildId || ''}
                  onChange={(e) => setEditingDevice({ ...editingDevice, buildId: e.target.value })}
                  fullWidth
                />
                <TextField
                  label="Build Description"
                  value={editingDevice.buildDescription || ''}
                  onChange={(e) => setEditingDevice({ ...editingDevice, buildDescription: e.target.value })}
                  fullWidth
                />
                <TextField
                  label="Build Date UTC"
                  value={editingDevice.buildDateUtc || ''}
                  onChange={(e) => setEditingDevice({ ...editingDevice, buildDateUtc: e.target.value })}
                  fullWidth
                />
                <TextField
                  label="Build Flavor"
                  value={editingDevice.buildFlavor || ''}
                  onChange={(e) => setEditingDevice({ ...editingDevice, buildFlavor: e.target.value })}
                  fullWidth
                />
                <TextField
                  label="Fingerprint"
                  value={editingDevice.fingerprint || ''}
                  onChange={(e) => setEditingDevice({ ...editingDevice, fingerprint: e.target.value })}
                  fullWidth
                />
                <TextField
                  label="Security Path"
                  value={editingDevice.securityPath || ''}
                  onChange={(e) => setEditingDevice({ ...editingDevice, securityPath: e.target.value })}
                  fullWidth
                />
                <TextField
                  label="Baseband"
                  value={editingDevice.baseband || ''}
                  onChange={(e) => setEditingDevice({ ...editingDevice, baseband: e.target.value })}
                  fullWidth
                />
                <TextField
                  label="Bootloader"
                  value={editingDevice.bootloader || ''}
                  onChange={(e) => setEditingDevice({ ...editingDevice, bootloader: e.target.value })}
                  fullWidth
                />
                <TextField
                  label="MAC Address"
                  value={editingDevice.mac_address || ''}
                  onChange={(e) => setEditingDevice({ ...editingDevice, mac_address: e.target.value })}
                  fullWidth
                />
                <TextField
                  label="Safety Net"
                  value={editingDevice.safety_net || ''}
                  onChange={(e) => setEditingDevice({ ...editingDevice, safety_net: e.target.value })}
                  fullWidth
                />
                <TextField
                  label="GPU"
                  value={editingDevice.gpu || ''}
                  onChange={(e) => setEditingDevice({ ...editingDevice, gpu: e.target.value })}
                  fullWidth
                />
                <TextField
                  label="Name"
                  value={editingDevice.name || ''}
                  onChange={(e) => setEditingDevice({ ...editingDevice, name: e.target.value })}
                  fullWidth
                />
                <TextField
                  label="Country"
                  value={editingDevice.country || ''}
                  onChange={(e) => setEditingDevice({ ...editingDevice, country: e.target.value })}
                  fullWidth
                />
                <TextField
                  label="Custom Note"
                  value={editingDevice.custom_note || ''}
                  onChange={(e) => setEditingDevice({ ...editingDevice, custom_note: e.target.value })}
                  fullWidth
                  multiline
                  rows={2}
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEditSave} color="primary" variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};